import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ColourPaletteResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetColourPalettesQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC colour palette data to match the GraphQL query structure
 */
function mapTrpcColourPalettesToGraphQL(
  trpcData: ColourPaletteResponseRow[] | undefined
): GetColourPalettesQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    colour_palette: trpcData,
  };
}

export const useGetColourPalettes = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.colourPalette.getColourPalettes.queryOptions(),

    enabled: trpcEnabled && !skip,
  });

  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);
  const data = mapTrpcColourPalettesToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
